import { useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ThumbsUp, ThumbsDown, MessageSquarePlus, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  useDeliveredBriefs,
  useRolePrebriefs,
  useRecordFeedback,
  type DeliveredOutput,
  type FeedbackSignal,
} from "@/hooks/useResearchHubBriefs";
import { HubSection, EmptyState, ErrorState, LoadingState, outputKind, surfaceLabel, formatDay } from "@/components/research-hub";

export default function MyBriefs() {
  const briefs = useDeliveredBriefs();
  const prebriefs = useRolePrebriefs();

  return (
    <div className="space-y-8 max-w-3xl">
      <HubSection title="Briefs delivered to you" description="Everything the hub has sent your way.">
        {briefs.isLoading ? (
          <LoadingState rows={2} />
        ) : briefs.error ? (
          <ErrorState onRetry={() => briefs.refetch()} />
        ) : (briefs.data ?? []).length === 0 ? (
          <EmptyState title="No briefs have been delivered yet." body="When the team sends you a brief, it will appear here." />
        ) : (
          <div className="space-y-3">
            {briefs.data!.map((b) => (
              <BriefCard key={b.id} brief={b} />
            ))}
          </div>
        )}
      </HubSection>

      <HubSection title="Your daily digest" description="A short read on what's material for you today.">
        {prebriefs.isLoading ? (
          <LoadingState rows={1} />
        ) : prebriefs.error ? (
          <ErrorState onRetry={() => prebriefs.refetch()} />
        ) : (prebriefs.data ?? []).length === 0 ? (
          <EmptyState title="No digest for today yet" body="Check back later — the digest is prepared each morning." />
        ) : (
          <div className="space-y-3">
            {prebriefs.data!.map((p, i) => (
              <Card key={i} className="p-4 space-y-2">
                <div className="font-heading text-sm font-medium">{surfaceLabel(p.surface)}</div>
                {p.has_signal === false ? (
                  <p className="font-body text-sm text-muted-foreground">Nothing material for you today.</p>
                ) : p.digest_markdown ? (
                  <div className="prose prose-sm max-w-none font-body prose-headings:font-heading prose-a:text-primary">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{p.digest_markdown}</ReactMarkdown>
                  </div>
                ) : (
                  <p className="font-body text-sm text-muted-foreground">Nothing material for you today.</p>
                )}
              </Card>
            ))}
          </div>
        )}
      </HubSection>
    </div>
  );
}

function BriefCard({ brief }: { brief: DeliveredOutput }) {
  return (
    <Card className="p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-heading text-base font-medium truncate">{brief.title || outputKind(brief.output_kind)}</div>
          <div className="font-body text-xs text-muted-foreground">
            {outputKind(brief.output_kind)} · delivered {formatDay(brief.sent_at)}
          </div>
        </div>
        {brief.asana_permalink && (
          <Button variant="outline" size="sm" asChild>
            <a href={brief.asana_permalink} target="_blank" rel="noopener noreferrer">
              Open in Asana <ExternalLink className="h-3.5 w-3.5 ml-1.5" />
            </a>
          </Button>
        )}
      </div>
      {brief.send_error && (
        <div className="font-body text-[11px] text-[hsl(var(--financial-gold))]">There was a delivery issue with this brief.</div>
      )}
      {brief.id && <FeedbackBar draftId={brief.id} />}
    </Card>
  );
}

function FeedbackBar({ draftId }: { draftId: string }) {
  const record = useRecordFeedback();
  const [chosen, setChosen] = useState<FeedbackSignal | null>(null);
  const [comment, setComment] = useState("");
  const [open, setOpen] = useState(false);

  function submit(signal: FeedbackSignal, extra?: { comment?: string }) {
    setChosen(signal);
    record.mutate(
      { draftId, signal, comment: extra?.comment ?? null },
      {
        onSuccess: () => {
          toast.success("Thanks — noted.");
          if (extra?.comment) {
            setComment("");
            setOpen(false);
          }
        },
        onError: () => {
          setChosen(null);
          toast.error("Couldn't save that just now.");
        },
      },
    );
  }

  return (
    <div className="flex items-center gap-1 pt-1 border-t">
      <span className="font-body text-[11px] text-muted-foreground mr-1">Was this useful?</span>
      <Button
        variant="ghost"
        size="sm"
        className={cn("h-7 px-2", chosen === "thumb_up" && "text-primary")}
        onClick={() => submit("thumb_up")}
        aria-label="Useful"
      >
        <ThumbsUp className="h-3.5 w-3.5" />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        className={cn("h-7 px-2", chosen === "thumb_down" && "text-destructive")}
        onClick={() => submit("thumb_down")}
        aria-label="Not useful"
      >
        <ThumbsDown className="h-3.5 w-3.5" />
      </Button>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="ghost" size="sm" className="h-7 px-2">
            <MessageSquarePlus className="h-3.5 w-3.5" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-72 space-y-2">
          <Textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Add a note for the team…"
            rows={3}
            className="resize-none font-body text-sm"
          />
          <Button size="sm" disabled={!comment.trim()} onClick={() => submit("comment", { comment: comment.trim() })}>
            Send note
          </Button>
        </PopoverContent>
      </Popover>
    </div>
  );
}
